import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Box,
  Typography,
  Autocomplete,
  TextField,
  MenuItem,
} from "@mui/material";
import {
  createDocumentList,
  updateDocumentList,
  deleteDocumentList,
  buildRegisterDocNumber,
  getRegisterDocSource,
  resolveDocumentUid,
  resolveDocumentNameAbbreviation,
  ensureDocumentIdentifierUid,
  documentAbbreviations,
  fetchPlantRecord,
  fetchLatestWbsRevision,
} from "./docListAPI";
import EditableTable from "../../../../../../../components/Tables/EditableTable";
import { useDCS } from "../DCSContext";
import { fetchPlantProjectChoices } from "../../UUIDRegister/uuidRegisterApi";

const abbreviationByCode = new Map(
  documentAbbreviations.map((item) => [
    String(item.abbreviation ?? "").trim(),
    item,
  ]),
);

function findDocumentNameOption(abbrOrUid) {
  const code = resolveDocumentNameAbbreviation(abbrOrUid);
  if (!code) return null;
  const known = abbreviationByCode.get(code);
  if (known) return known;
  return {
    sNo: `legacy-${code}`,
    description: `${code} (current)`,
    abbreviation: code,
  };
}

function buildDocumentNameOptions(currentAbbr) {
  const option = findDocumentNameOption(currentAbbr);
  if (option && !abbreviationByCode.has(currentAbbr)) {
    return [option, ...documentAbbreviations];
  }
  return documentAbbreviations;
}

// row._duidListeners is a tiny pub/sub so the DUID preview field can
// re-render when a sibling field (WBS, Document Identifier) changes.
// MRT's own row._valuesCache + setCreatingRow(row) does NOT work for this:
// setCreatingRow is called with the very same (in-place-mutated) row
// reference, so React's setState bails out via Object.is and nothing
// re-renders. Subscribing components force their own re-render instead.
function notifyDuidListeners(row, duid) {
  if (!Array.isArray(row._duidListeners)) return;
  row._duidListeners.forEach((fn) => {
    try {
      fn(duid);
    } catch {
      /* ignore */
    }
  });
}

function createDocumentIdentifierEdit(existingDocuments) {
  return function DocumentIdentifierEdit({ cell, column, row }) {
    const readStored = () =>
      String(
        row._valuesCache?.documentName ??
          row._valuesCache?.[column.id] ??
          row.original?.documentName ??
          row.original?.uid ??
          row.original?.department ??
          cell.getValue() ??
          "",
      ).trim();

    const [selected, setSelected] = React.useState(() =>
      findDocumentNameOption(readStored()),
    );

    React.useEffect(() => {
      if (!row._valuesCache) {
        row._valuesCache = {};
      }

      const abbr = selected?.abbreviation ?? "";
      if (!abbr) {
        row._valuesCache[column.id] = "";
        row._valuesCache.documentName = "";
        row._valuesCache.uid = "";
        notifyDuidListeners(row, "");
        return;
      }

      const ensured = ensureDocumentIdentifierUid({ documentName: abbr });
      const duid = ensured.documentName || "";
      // Column value stays the description for display; what is persisted is
      // the identifier's code ("BOM"), which is the whole UID now.
      row._valuesCache[column.id] = selected?.description ?? abbr;
      row._valuesCache.documentName = duid;
      row._valuesCache.uid = duid;
      notifyDuidListeners(row, duid);
    }, [selected, column.id, row]);

    const options = buildDocumentNameOptions(
      resolveDocumentNameAbbreviation(readStored()),
    );

    // An identifier names one document per WBS revision - one Bill of
    // Materials, one Estimation Summary - and the UID carries no sequence to
    // tell two of them apart any more. So the identifiers already filed under
    // this row's WBS and revision are offered as taken.
    //
    // A document whose WBS cannot be read is ignored rather than assumed to
    // clash: better to allow a duplicate than to block a row with no way to
    // explain why.
    const rowProjectId = String(
      row._valuesCache?.projectId ?? row.original?.projectId ?? "",
    ).trim();
    const rowRevision = String(
      row._valuesCache?.revision ?? row.original?.revision ?? "",
    ).trim();

    const takenCodes = new Set();
    if (rowProjectId) {
      (existingDocuments || []).forEach((doc) => {
        if (String(doc?.id ?? "") === String(row.original?.id ?? "")) return;

        const docProjectId = String(
          doc?.projectId ?? doc?.project?.id ?? "",
        ).trim();
        if (!docProjectId || docProjectId !== rowProjectId) return;
        if (String(doc?.revision ?? "").trim() !== rowRevision) return;

        const code = resolveDocumentNameAbbreviation(
          doc?.documentName ?? doc?.uid ?? "",
        );
        if (code) takenCodes.add(code);
      });
    }

    return (
      <Autocomplete
        size="small"
        fullWidth
        options={options}
        value={selected}
        onChange={(_event, option) => setSelected(option)}
        getOptionLabel={(option) => option?.description ?? ""}
        getOptionDisabled={(option) =>
          takenCodes.has(String(option?.abbreviation ?? "").trim())
        }
        isOptionEqualToValue={(a, b) =>
          String(a?.abbreviation ?? "") === String(b?.abbreviation ?? "")
        }
        filterOptions={(opts, { inputValue }) => {
          const q = inputValue.trim().toUpperCase();
          if (!q) return opts;
          return opts.filter(
            (o) =>
              String(o.description ?? "")
                .toUpperCase()
                .includes(q) ||
              String(o.abbreviation ?? "")
                .toUpperCase()
                .includes(q),
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Document Identifier"
            placeholder="Select document type…"
            variant="standard"
          />
        )}
        sx={{ minWidth: 280, width: "100%" }}
      />
    );
  };
}

// The key the WBS picker marks a row with when the user picks a different
// WBS in it. Only then does an edit carry a WBS change: a document's stored
// project id is not always one the register's own (client-filtered) list
// holds, and sending it back on an ordinary edit is how a plain save ended
// up refused with "there's no project with this id".
const WBS_MOVED_KEY = "__wbsMoved";

// WBS (plant mode): writes the chosen projectId into row._valuesCache and
// notifies the DUID preview so it recomputes.
//
// The field reports a WBS rather than asking for one - filled in and
// disabled, the way PUID reports the plant - whenever the WBS is already
// settled, which is both of these:
//
//   * editing a document: it is filed under the WBS it was created under,
//     and the register is not where a document gets moved between them;
//   * a new row opened from the projects tree's "Add Document"
//     (`lockedWbs`): that click already said which WBS this is.
//
// What is left is a new row on a register opened on its own - the one case
// where the WBS is still an open question, and the picker stands.
function createWbsProjectEdit(projectChoices, lockedWbs) {
  return function WbsProjectEdit({ column, row }) {
    const isExisting = Boolean(row?.original?.id);
    const locked = isExisting || Boolean(lockedWbs?.id);

    const readStored = () =>
      String(
        (locked && !isExisting ? lockedWbs.id : null) ??
          row._valuesCache?.[column.id] ??
          row.original?.projectId ??
          "",
      ).trim();

    // A document saved with no WBS of its own - and an edit of it would
    // otherwise open on an empty picker, with no way to tell that the field
    // is unset rather than still loading. The page's own WBS stands in: the
    // one the tree named, or else the first of the plant's.
    const fallbackWbs = String(
      lockedWbs?.id || projectChoices[0]?.id || "",
    ).trim();

    const [value, setValue] = React.useState(
      () => readStored() || fallbackWbs,
    );

    React.useEffect(() => {
      if (!row._valuesCache) row._valuesCache = {};
      row._valuesCache[column.id] = value;
      // A WBS filled in for a document that had none is a WBS the save has to
      // carry, the same as one picked by hand (see WBS_MOVED_KEY).
      if (value && !String(row.original?.projectId ?? "").trim()) {
        row._valuesCache[WBS_MOVED_KEY] = true;
      }
      notifyDuidListeners(row, "");
    }, [value, column.id, row]);

    if (locked) {
      // By the id the field holds first, so an edited document reads under
      // its own WBS rather than whichever one the page was opened for.
      const name =
        projectChoices.find((project) => String(project.id) === String(value))
          ?.name ||
        (isExisting ? resolveModuleLabel(row, projectChoices) : "") ||
        lockedWbs?.name ||
        value;

      return (
        <TextField
          size="small"
          fullWidth
          label="WBS"
          variant="standard"
          value={name}
          disabled
        />
      );
    }

    // The row's own WBS is always one of the options. The fetched list
    // keeps only projects whose clientName matches this build's, so a row
    // filed under any other one would otherwise select nothing - MUI
    // reports it as an "out-of-range value" and the field reads empty, as
    // though the document belonged nowhere.
    const options =
      !value || projectChoices.some((p) => String(p.id) === String(value))
        ? projectChoices
        : [
            {
              id: value,
              name: resolveModuleLabel(row, projectChoices) || value,
            },
            ...projectChoices,
          ];

    return (
      <TextField
        select
        size="small"
        fullWidth
        label="WBS"
        variant="standard"
        value={value}
        onChange={(e) => {
          if (!row._valuesCache) row._valuesCache = {};
          row._valuesCache[WBS_MOVED_KEY] = true;
          setValue(e.target.value);
        }}
      >
        {options.map((project) => (
          <MenuItem key={project.id} value={project.id}>
            {project.name}
          </MenuItem>
        ))}
      </TextField>
    );
  };
}

// Latest Revision - the newest revision of the WBS the row sits under, which
// is what the column's name has always claimed it is. Three things can fill
// it, in this order:
//
//   1. the revision the projects tree named (`lockedRevision`), on a new row:
//      it came with the click, so the field reports it rather than asking
//      again, disabled for the same reason the locked WBS field is;
//   2. that WBS's newest revision, resolved here (see fetchLatestWbsRevision)
//      - and on an existing document this is the field, disabled beside its
//      equally settled WBS: the document is filed under a WBS, the WBS's
//      newest revision is a fact about that WBS, and neither is something an
//      edit of the document decides;
//   3. the row's own stored revision, which stands when (2) has nothing to
//      say - a WBS with no revisions saved yet - and on a new row until the
//      lookup answers.
//
// (2) is what makes the field fill itself on a plain "Add Doc": the tree only
// knows a WBS's newest revision once that WBS has been expanded, so before
// this the field came up empty unless the register had been opened from the
// tree. It also re-fills whenever the WBS picker beside it changes.
//
// Written into _valuesCache because a disabled field is still a field this
// row has to be saved with (MRT saves what the edit components wrote, not the
// row's starting values).
function createRevisionEdit(lockedRevision, projectChoices, plantId) {
  return function RevisionEdit({ column, row }) {
    const isExisting = Boolean(row?.original?.id);
    // The tree's revision is the whole answer for the row it opened - no
    // lookup, nothing to change. Editing is settled too, but by the lookup.
    const lockedByTree = Boolean(lockedRevision) && !isExisting;

    const readStored = () =>
      String(
        (lockedByTree ? lockedRevision : null) ??
          row._valuesCache?.[column.id] ??
          row.original?.revision ??
          "",
      ).trim();

    const readWbs = () =>
      String(
        row._valuesCache?.projectId ?? row.original?.projectId ?? "",
      ).trim();

    const [value, setValue] = React.useState(readStored);
    // The WBS the field is resolving against, and that WBS's newest revision.
    const [wbs, setWbs] = React.useState(readWbs);
    const [latest, setLatest] = React.useState("");
    const [resolving, setResolving] = React.useState(!lockedByTree);
    const openedWithWbs = React.useRef(readWbs());

    // Typed into only as a last resort. A revision the field could put there
    // itself is not a question to ask - whether it came with the tree's click
    // or from the lookup - and which of those two it was is an accident of
    // where a discipline keeps its revisions: an Estimation WBS carries them
    // in the WBS list the tree already has, so its "Add Document" can name
    // the revision in the URL, while PV's arrive from their own request and
    // only once that WBS has been expanded. Keying "read-only" off the
    // lookup instead of off the URL is what keeps the two alike. What is
    // left editable is a WBS with no revisions saved at all, where the
    // alternative is a field that can't be filled.
    const readOnly = lockedByTree || isExisting || resolving || Boolean(latest);

    // The WBS picker announces its changes down the DUID channel (it has to -
    // the DUID is built from the WBS), so listening there is how this field
    // hears that it is now being filled in for a different WBS.
    React.useEffect(() => {
      if (!Array.isArray(row._duidListeners)) row._duidListeners = [];
      const listener = () =>
        setWbs(
          String(
            row._valuesCache?.projectId ?? row.original?.projectId ?? "",
          ).trim(),
        );
      row._duidListeners.push(listener);
      return () => {
        row._duidListeners = (row._duidListeners || []).filter(
          (fn) => fn !== listener,
        );
      };
    }, [row]);

    React.useEffect(() => {
      if (lockedByTree || !wbs) {
        setLatest("");
        setResolving(false);
        return undefined;
      }

      let cancelled = false;
      const type = (projectChoices || []).find(
        (project) => String(project.id) === wbs,
      )?.type;

      setResolving(true);
      fetchLatestWbsRevision(wbs, { type, plantId })
        .then((revision) => {
          if (!cancelled) setLatest(String(revision ?? "").trim());
        })
        .catch(() => {
          if (!cancelled) setLatest("");
        })
        .finally(() => {
          if (!cancelled) setResolving(false);
        });

      return () => {
        cancelled = true;
      };
    }, [lockedByTree, wbs]);

    // An existing document takes the revision it is told; a new row fills an
    // empty field, and re-fills one whose WBS the user changed in the picker
    // - a revision belongs to the WBS it was read from. What was typed into a
    // new row stands. Nothing overwrites a stored revision with nothing: a
    // WBS with no revisions saved yet leaves the field as it found it.
    React.useEffect(() => {
      if (lockedByTree || !latest) return;
      const movedWbs = wbs !== openedWithWbs.current;
      setValue((current) =>
        isExisting || movedWbs || current.trim() === "" ? latest : current,
      );
    }, [latest, wbs, lockedByTree, isExisting]);

    React.useEffect(() => {
      if (!row._valuesCache) row._valuesCache = {};
      row._valuesCache[column.id] = value;
      // The revision closes the DUID, so its preview has to recompute.
      notifyDuidListeners(row, "");
    }, [value, column.id, row]);

    return (
      <TextField
        size="small"
        fullWidth
        label="Latest Revision"
        variant="standard"
        value={value}
        disabled={readOnly}
        onChange={(e) => setValue(e.target.value)}
        helperText={
          readOnly || resolving
            ? undefined
            : "This WBS has no saved revisions yet"
        }
      />
    );
  };
}

// WBS free-text field (non-plant mode): same notify-on-change wiring.
function WbsProjectNumberEdit({ column, row }) {
  const readStored = () =>
    String(
      row._valuesCache?.[column.id] ?? row.original?.projectNumber ?? "",
    ).trim();

  const [value, setValue] = React.useState(readStored);

  React.useEffect(() => {
    if (!row._valuesCache) row._valuesCache = {};
    row._valuesCache[column.id] = value;
    notifyDuidListeners(row, "");
  }, [value, column.id, row]);

  return (
    <TextField
      size="small"
      fullWidth
      label="WBS"
      variant="standard"
      value={value}
      onChange={(e) => setValue(e.target.value)}
    />
  );
}

// Read-only DUID preview. Subscribes to row._duidListeners so it re-renders
// whenever the WBS or Document Identifier fields change.
function createDocumentNumberPreview(fixedPlantName, projectChoices) {
  return function DocumentNumberPreview({ row }) {
    const [, forceTick] = React.useState(0);

    React.useEffect(() => {
      if (!Array.isArray(row._duidListeners)) row._duidListeners = [];
      const listener = () => forceTick((t) => t + 1);
      row._duidListeners.push(listener);
      return () => {
        row._duidListeners = (row._duidListeners || []).filter(
          (fn) => fn !== listener,
        );
      };
    }, [row]);

    const source = getRegisterDocSource(row);
    const wbsLabel = resolveModuleLabel(row, projectChoices);
    const hasWbs = wbsLabel.trim() !== "";
    const hasDocIdentifier = String(source.uid ?? "").trim() !== "";
    if (!hasWbs || !hasDocIdentifier) return null;

    const resolved = buildRegisterDocNumber(
      {
        ...source,
        projectNumber:
          fixedPlantName || String(source.projectNumber ?? "").trim(),
      },
      { wbsLabel },
    );
    return (
      <TextField
        label="DUID"
        value={resolved}
        variant="standard"
        fullWidth
        disabled
        InputLabelProps={{ shrink: true }}
      />
    );
  };
}

export const DocLsitInitialData = {
  companyName: "",
  projectNumber: "",
  documentName: "",
  uid: "",
  revision: "",
  docRefNumber: "",
  documentNumber: "",
  description: "",
  docRevision: "",
  projectId: "",
};

const headerCenterSx = {
  "& .Mui-TableHeadCell-Content": {
    justifyContent: "center",
  },
  textAlign: "center",
  fontWeight: 700,
};

const editLabelProps = (label) => ({
  muiEditTextFieldProps: { label },
});

const resolveModuleLabel = (row, projectChoices = []) => {
  const cache = row?._valuesCache ?? {};
  const original = row?.original ?? row ?? {};
  const projectId = String(cache.projectId ?? original.projectId ?? "").trim();
  const fromChoice = projectChoices.find((p) => String(p.id) === projectId);
  const label =
    fromChoice?.name ||
    cache.projectName ||
    original.projectName ||
    cache.projectNumber ||
    original.projectNumber ||
    "";
  return String(label ?? "").trim();
};

export const DocListColumns = (
  _allUsers,
  _type,
  {
    plant = false,
    plantName = "",
    projectChoices = [],
    existingDocuments = [],
    lockedWbs = null,
    lockedRevision = "",
    plantId = "",
  } = {},
) => {
  const fixedPlantName = String(plantName ?? "").trim();
  const DocumentIdentifierEdit =
    createDocumentIdentifierEdit(existingDocuments);
  const DocumentNumberPreview = createDocumentNumberPreview(
    fixedPlantName,
    projectChoices,
  );

  const moduleColumn = plant
    ? {
        id: "projectId",
        accessorKey: "projectId",
        header: "WBS",
        size: 28,
        enableEditing: true,
        Edit: createWbsProjectEdit(projectChoices, lockedWbs),
        muiTableHeadCellProps: { sx: { ...headerCenterSx } },
        accessorFn: (row) => resolveModuleLabel(row, projectChoices),
        Cell: ({ row }) => {
          const v = resolveModuleLabel(row.original ?? row, projectChoices);
          return v !== "" ? v : "—";
        },
      }
    : {
        id: "projectNumber",
        accessorKey: "projectNumber",
        header: "WBS",
        size: 20,
        enableEditing: true,
        Edit: WbsProjectNumberEdit,
        muiTableHeadCellProps: { sx: { ...headerCenterSx } },
        accessorFn: (row) => String(row.projectNumber ?? "").trim(),
        Cell: ({ row }) => {
          const v = String(
            row.original?.projectNumber ?? row.projectNumber ?? "",
          ).trim();
          return v !== "" ? v : "—";
        },
      };

  const puidColumn = {
    id: "puid",
    header: "PUID",
    size: 24,
    enableEditing: false,
    // "PUID" heads the column; the dialogs call the same field "Project",
    // which is what it holds - the plant this register belongs to.
    ...editLabelProps("Project"),
    muiTableHeadCellProps: { sx: { ...headerCenterSx } },
    accessorFn: (row) =>
      fixedPlantName || String(row.projectNumber ?? "").trim(),
    Cell: () => (fixedPlantName ? fixedPlantName : "—"),
  };

  return [
    puidColumn,
    moduleColumn,
    {
      // Hidden in the grid (the DUID column already spells the number out),
      // but kept as a column so the Add/Edit dialogs still offer the
      // identifier picker - nothing else sets documentName/uid, and the DUID
      // cannot be built without them. See DOC_REGISTER_HIDDEN_COLUMNS below.
      id: "docNo",
      header: "Doc No",
      size: 28,
      enableEditing: true,
      Edit: DocumentIdentifierEdit,
      muiTableHeadCellProps: {
        sx: {
          ...headerCenterSx,
          backgroundColor: "#FFE0B2",
        },
      },
      accessorFn: (row) =>
        resolveDocumentUid(row) ||
        String(row.documentName ?? row.uid ?? "").trim(),
      Cell: ({ row }) => {
        const uid = resolveDocumentUid(
          row?.original ? getRegisterDocSource(row) : row,
        );
        return uid !== "" ? uid : "—";
      },
    },
    {
      id: "documentNumber",
      header: "DUID",
      size: 52,
      enableEditing: false,
      Edit: DocumentNumberPreview,
      muiTableHeadCellProps: {
        sx: {
          ...headerCenterSx,
          backgroundColor: "#C8E6C9",
        },
      },
      accessorFn: (row) =>
        buildRegisterDocNumber(
          {
            projectNumber:
              fixedPlantName || String(row.projectNumber ?? "").trim(),
            documentName: row.documentName,
            uid: row.uid,
            revision: row.revision,
          },
          { wbsLabel: resolveModuleLabel(row, projectChoices) },
        ),
      Cell: ({ row }) => {
        const source = getRegisterDocSource(row);
        const resolved = buildRegisterDocNumber(
          {
            ...source,
            projectNumber:
              fixedPlantName || String(source.projectNumber ?? "").trim(),
          },
          { wbsLabel: resolveModuleLabel(row, projectChoices) },
        );
        return resolved !== "" ? resolved : "—";
      },
    },
    {
      accessorKey: "revision",
      header: "Latest Revision",
      size: 15,
      muiTableHeadCellProps: { sx: { ...headerCenterSx } },
      ...editLabelProps("Latest Revision"),
      // Always this editor, not only when the tree named a revision: it is
      // what resolves the WBS's newest revision by itself.
      Edit: createRevisionEdit(lockedRevision, projectChoices, plantId),
    },
    // The three columns below are the client's own numbering of the
    // document, kept beside this register's: they are named as the client
    // names them, and stored under the keys they always were
    // (docRefNumber / description / docRevision).
    {
      accessorKey: "docRefNumber",
      header: "Client Doc",
      size: 24,
      muiTableHeadCellProps: { sx: { ...headerCenterSx } },
      ...editLabelProps("Client Doc"),
    },
    {
      id: "description",
      accessorFn: (row) => row.description ?? row.title ?? "",
      header: "Client Doc Description",
      size: 36,
      muiTableHeadCellProps: {
        sx: {
          ...headerCenterSx,
          backgroundColor: "#BBDEFB",
        },
      },
      ...editLabelProps("Client Doc Description"),
    },
    {
      // This row's own revision, beside its description - not the
      // "Latest Revision" column above, which is the document's.
      accessorKey: "docRevision",
      header: "Client Revision",
      size: 15,
      muiTableHeadCellProps: { sx: { ...headerCenterSx } },
      ...editLabelProps("Client Revision"),
    },
  ];
};

// Columns the register defines for the Add/Edit dialogs only: MRT builds the
// dialog fields from every data column, visible or not, so hiding one here
// keeps its editor in the dialog while leaving the grid alone.
const DOC_REGISTER_HIDDEN_COLUMNS = { docNo: false };

/**
 * `defaultProjectId` / `defaultRevision` come from the projects tree: it
 * opens this page from a WBS row's "Add Document", so a new row already
 * knows which WBS it belongs to and which revision of it is the newest.
 * Both only seed the create dialog - either can still be changed there.
 */
const DocumentList = ({
  defaultProjectId = "",
  defaultProjectName = "",
  defaultRevision = "",
}) => {
  const {
    allUsers,
    type,
    activeProjectId,
    plant,
    plantId,
    documentList,
    getDocList,
    documentListLoaded,
  } = useDCS();

  const [uuidProjectChoices, setUuidProjectChoices] = useState([]);
  const [plantName, setPlantName] = useState("");

  const documentListRef = useRef(documentList);
  documentListRef.current = documentList;
  const documentListLoadedRef = useRef(documentListLoaded);
  documentListLoadedRef.current = documentListLoaded;

  const defaultPlantProjectIdRef = useRef("");
  const plantNameRef = useRef("");
  const needsNetworkFetchRef = useRef(false);

  const filterDocumentsByType = useCallback(
    (list) => {
      if (!type || type === "all") return list || [];
      return (list || []).filter((doc) => doc.type === type);
    },
    [type],
  );

  const existingDocuments = useMemo(
    () => filterDocumentsByType(documentList || []),
    [documentList, filterDocumentsByType],
  );

  useEffect(() => {
    if (!plant || !plantId) {
      setPlantName("");
      return;
    }
    let cancelled = false;
    fetchPlantRecord(plantId)
      .then((plant) => {
        if (!cancelled) setPlantName(plant?.name || "");
      })
      .catch(() => {
        if (!cancelled) setPlantName("");
      });
    return () => {
      cancelled = true;
    };
  }, [plant, plantId]);

  useEffect(() => {
    if (!plant || !plantId) {
      setUuidProjectChoices([]);
      return undefined;
    }

    let cancelled = false;

    const loadProjects = async () => {
      try {
        const choices = await fetchPlantProjectChoices(plantId);
        if (!cancelled) setUuidProjectChoices(choices);
      } catch (e) {
        console.warn("Document Register project choices load failed:", e);
        if (!cancelled) setUuidProjectChoices([]);
      }
    };

    loadProjects();
    return () => {
      cancelled = true;
    };
  }, [plant, plantId]);

  // The WBS the tree named is offered whether or not the fetched list
  // holds it: that fetch keeps only projects whose clientName matches this
  // build's, and a WBS written by another tool can carry a different one
  // (or none). The click already said which WBS this is, so the dialog has
  // to be able to show it - and it is still just a default, changeable in
  // the dropdown like any other.
  const projectChoices = useMemo(() => {
    const named = String(defaultProjectId ?? "").trim();
    if (!named) return uuidProjectChoices;
    if (uuidProjectChoices.some((p) => String(p.id) === named)) {
      return uuidProjectChoices;
    }

    return [
      { id: named, name: String(defaultProjectName || named).trim() },
      ...uuidProjectChoices,
    ];
  }, [defaultProjectId, defaultProjectName, uuidProjectChoices]);

  const defaultPlantProjectId =
    String(defaultProjectId ?? "").trim() ||
    (projectChoices.length > 0 ? projectChoices[0].id : "");

  // What a new row's WBS and Latest Revision are fixed to, when this page was
  // opened from a WBS row in the projects tree.
  const lockedWbs = useMemo(() => {
    const id = String(defaultProjectId ?? "").trim();
    if (!id) return null;

    const name =
      projectChoices.find((project) => String(project.id) === id)?.name ||
      defaultProjectName ||
      id;

    return { id, name };
  }, [defaultProjectId, defaultProjectName, projectChoices]);

  defaultPlantProjectIdRef.current = defaultPlantProjectId;
  plantNameRef.current = plantName;

  const columns = useMemo(
    () =>
      DocListColumns(allUsers, type, {
        plant,
        plantName,
        projectChoices,
        existingDocuments,
        lockedWbs,
        lockedRevision: defaultRevision,
        plantId,
      }),
    [
      allUsers,
      type,
      plant,
      plantName,
      projectChoices,
      existingDocuments,
      lockedWbs,
      defaultRevision,
      plantId,
    ],
  );

  const parentId = plant && plantId ? plantId : activeProjectId;

  const getItems = useCallback(async () => {
    if (
      !needsNetworkFetchRef.current &&
      documentListLoadedRef.current &&
      documentListRef.current != null
    ) {
      return filterDocumentsByType(documentListRef.current);
    }

    needsNetworkFetchRef.current = false;
    const list = await getDocList();
    return filterDocumentsByType(list ?? documentListRef.current ?? []);
  }, [filterDocumentsByType, getDocList]);

  const withFixedProjectRef = useCallback((item) => {
    const name = String(plantNameRef.current ?? "").trim();
    if (!name) return item;
    return { ...item, projectNumber: name };
  }, []);

  const handleCreateItem = useCallback(
    async (createParentId, item) => {
      needsNetworkFetchRef.current = true;

      const withUid = withFixedProjectRef(ensureDocumentIdentifierUid(item));

      if (plant && plantId) {
        const selectedProjectId =
          String(withUid.projectId ?? "").trim() ||
          defaultPlantProjectIdRef.current;
        if (!selectedProjectId) {
          throw new Error("No WBS available for this document register row.");
        }
        const wbsLabel = resolveModuleLabel(withUid, projectChoices);
        const { projectId, projectName, companyName, ...docData } = withUid;
        return createDocumentList(type, selectedProjectId, docData, {
          wbsLabel,
        });
      }
      return createDocumentList(type, createParentId, withUid);
    },
    [plant, plantId, type, withFixedProjectRef, projectChoices],
  );

  const handleUpdateItem = useCallback(
    async (updateDocument) => {
      needsNetworkFetchRef.current = true;
      const withUid = withFixedProjectRef(
        ensureDocumentIdentifierUid(updateDocument),
      );
      const wbsLabel = resolveModuleLabel(withUid, projectChoices);

      // A WBS change travels only when the user picked one in the dialog.
      // Not inferred by comparing ids: the stored id and the one the picker
      // resolves to can differ for reasons that have nothing to do with the
      // user moving anything (see WBS_MOVED_KEY).
      const movedToProjectId = updateDocument?.[WBS_MOVED_KEY]
        ? String(withUid.projectId ?? "").trim()
        : "";

      return updateDocumentList(withUid, { wbsLabel, movedToProjectId });
    },
    [withFixedProjectRef, projectChoices],
  );

  const handleDeleteItem = useCallback(async (id) => {
    needsNetworkFetchRef.current = true;
    return deleteDocumentList(id);
  }, []);

  const tableApi = useMemo(
    () => ({
      getItems,
      createItem: handleCreateItem,
      updateItem: handleUpdateItem,
      deleteItem: handleDeleteItem,
    }),
    [getItems, handleCreateItem, handleUpdateItem, handleDeleteItem],
  );

  const initialData = useMemo(
    () => ({
      ...DocLsitInitialData,
      projectNumber: plantName || "",
      ...(defaultPlantProjectId ? { projectId: defaultPlantProjectId } : {}),
      ...(defaultRevision ? { revision: defaultRevision } : {}),
    }),
    [defaultPlantProjectId, defaultRevision, plantName],
  );

  return (
    <Box>
      {plant && plantName ? (
        <Typography variant="h4" color="darkBlue" sx={{ pb: 2 }}>
          {plantName}
        </Typography>
      ) : null}
      <EditableTable
        name="Doc Register"
        displayMode="modal"
        columns={columns}
        enableEditing={true}
        enableCreate={true}
        // A row is deleted outright here - the archive button beside it never
        // did anything, so it is left out rather than shown doing nothing.
        enableDelete
        enableArchive={false}
        initialData={initialData}
        api={tableApi}
        parentId={parentId}
        additionalProps={{
          initialState: { columnVisibility: DOC_REGISTER_HIDDEN_COLUMNS },
          defaultColumn: {
            muiEditTextFieldProps: ({ column }) => ({
              label:
                typeof column.columnDef.header === "string"
                  ? column.columnDef.header
                  : undefined,
            }),
          },
        }}
      />
    </Box>
  );
};

export default DocumentList;
